// App
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers';

type BedLabelKey = 'anyOrStudio' | 'oneOrMore' | 'twoOrMore' | 'threeOrMore' | 'fourOrMore';
type BathLabelKey = 'any' | 'oneOrMore' | 'oneHalfOrMore' | 'twoOrMore' | 'threeOrMore' | 'fourOrMore';

export const useBedButtonLabel = ({ bedsOnly = false } = {}): [() => string, (label: string) => boolean] => {
  // Redux
  const bedState = useSelector((state: RootReduxState) => state.filter.bedrooms);
  const bathState = useSelector((state: RootReduxState) => state.filter.bathrooms);
  const DEFAULT_LABEL = bedsOnly ? 'All beds' : 'All beds, all baths';

  const getButtonLabel = () => {
    const isExact = bedState.isExact;
    const bedIdentifiers = Object.keys(bedState) as Array<string>;
    const activeBeds = bedIdentifiers.filter((beds) => {
      return bedState[beds as keyof typeof bedState];
    })[0] as BedLabelKey;
    const bathIdentifiers = Object.keys(bathState) as Array<string>;
    const activeBaths = bathIdentifiers.filter((baths) => {
      return bathState[baths as keyof typeof bathState];
    })[0] as BathLabelKey;

    const BED_LABEL_MAP: Record<BedLabelKey, string> = {
      anyOrStudio: isExact ? 'Studio' : 'All beds',
      oneOrMore: isExact ? '1 bed' : '1+ beds',
      twoOrMore: isExact ? '2 beds' : '2+ beds',
      threeOrMore: isExact ? '3 beds' : '3+ beds',
      fourOrMore: isExact ? '4 beds' : '4+ beds',
    };

    const BATH_LABEL_MAP: Record<BathLabelKey, string> = {
      any: 'all baths',
      oneOrMore: '1+ baths',
      oneHalfOrMore: '1.5+ baths',
      twoOrMore: '2+ baths',
      threeOrMore: '3+ baths',
      fourOrMore: '4+ baths',
    };

    if (activeBeds === 'anyOrStudio' && activeBaths === 'any') {
      return DEFAULT_LABEL;
    } else {
      return bedsOnly ? BED_LABEL_MAP[activeBeds] : `${BED_LABEL_MAP[activeBeds]}, ${BATH_LABEL_MAP[activeBaths]}`;
    }
  };

  const getButtonStatus = (label: string) => {
    return label !== DEFAULT_LABEL;
  };

  return [getButtonLabel, getButtonStatus];
};
